// frontend/src/components/Recordings.js

import LoadingSpinner from './LoadingSpinner';
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const BATCH_SIZE = 10;

function Recordings() {
  const [recordings, setRecordings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadMoreLoading, setLoadMoreLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [startOffset, setStartOffset] = useState(0);
  const location = useLocation();

  useEffect(() => {
    fetchInitialRecordings();
    // Check if eventId parameter is present in the URL
    const params = new URLSearchParams(location.search);
    const eventId = params.get('eventId');
    if (eventId) {
      playRecording(eventId);
    }
  }, []);

  useEffect(() => {
    // Re-check for eventId after initial load completes
    const params = new URLSearchParams(location.search);
    const eventId = params.get('eventId');
    if (eventId && recordings.length > 0) {
      // Check if this event is already in the loaded recordings
      const found = recordings.find(r => r.id === eventId);
      if (!found) {
        // Try to find it by loading more batches
        findEventAndPlay(eventId, startOffset + BATCH_SIZE);
      }
    }
  }, [recordings.length]);

  const fetchInitialRecordings = async () => {
    try {
      const response = await fetch(`/api/recordings?start=0&count=${BATCH_SIZE}`);
      if (!response.ok) {
        throw new Error('Failed to fetch recordings');
      }
      const data = await response.json();
      setRecordings(data.events || []);
      setHasMore(data.hasMore || false);
      setStartOffset(BATCH_SIZE);
      setLoading(false);
    } catch (error) {
      console.error('Error:', error.message);
      setLoading(false);
    }
  };

  const fetchMoreRecordings = async () => {
    setLoadMoreLoading(true);
    try {
      const response = await fetch(`/api/recordings?start=${startOffset}&count=${BATCH_SIZE}`);
      if (!response.ok) {
        throw new Error('Failed to fetch more recordings');
      }
      const data = await response.json();
      const newEvents = data.events || [];
      setRecordings(prev => [...prev, ...newEvents]);
      setHasMore(data.hasMore || false);
      setStartOffset(prev => prev + BATCH_SIZE);
    } catch (error) {
      console.error('Error loading more:', error.message);
    } finally {
      setLoadMoreLoading(false);
    }
  };

  const findEventAndPlay = async (eventId, offset) => {
    if (!hasMore && offset >= startOffset) {
      return; // No more data to fetch
    }
    try {
      const response = await fetch(`/api/recordings?start=${offset}&count=${BATCH_SIZE}`);
      if (!response.ok) return;
      const data = await response.json();
      const events = data.events || [];
      const found = events.find(e => e.id === eventId);
      if (found) {
        setRecordings(prev => {
          // Only add if not already present
          const existingIds = new Set(prev.map(r => r.id));
          const uniqueEvents = events.filter(e => !existingIds.has(e.id));
          return [...prev, ...uniqueEvents];
        });
        playRecording(eventId);
        return;
      }
      // Not found in this batch, try next
      if (data.hasMore) {
        findEventAndPlay(eventId, offset + BATCH_SIZE);
      }
    } catch (error) {
      console.error('Error finding event:', error);
    }
  };

  const formatDuration = (durationInSeconds) => {
    const minutes = Math.floor(durationInSeconds / 60);
    const seconds = durationInSeconds % 60;
    return `${minutes} Minuten ${seconds} Sekunden`;
  };

  const playRecording = async (eventId) => {
    try {
      // Create video container
      const videoContainer = document.createElement('div');
      videoContainer.classList.add('video-container');
  
      // Create loading spinner
      const loadingSpinner = document.createElement('div');
      loadingSpinner.classList.add('loading-spinner');
      loadingSpinner.style.position = 'absolute';
      loadingSpinner.style.zIndex = '9999'; // Set the z-index dynamically
      videoContainer.appendChild(loadingSpinner); // Append loading spinner to video container
  
      // Append the video container to the .App element
      document.getElementById('root').appendChild(videoContainer);
      
      // Fetch recording stream
      const response = await fetch(`/api/media/${eventId}`);
      if (!response.ok) {
        throw new Error('Failed to fetch recording stream');
      }
      
      
      

      // Process fetched data
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
  
      // Create video element
      const video = document.createElement('video');
      video.controls = true;
      video.autoplay = true;
      video.style.maxWidth = '100%';
      video.style.display = 'block';
      video.style.margin = '0 auto';
  
      // Create source element for video
      const source = document.createElement('source');
      source.src = url;
      source.type = 'video/mp4';
  
      // Append source to video and video to video container
      video.appendChild(source);
      videoContainer.appendChild(video);

      video.addEventListener('canplaythrough', function() {
        // Hide loading spinner once fetch call completes
        loadingSpinner.style.display = 'none';
        
      });
  
      // Create close button
      const closeButton = document.createElement('button');
      closeButton.classList.add('close-button');
      closeButton.innerHTML = '&times;';
      closeButton.onclick = () => {
        videoContainer.remove();
        // Check if the URL contains the eventid parameter
        if (window.location.href.includes('?eventId=')) {
          // If yes, remove the eventid parameter and navigate back to /
          window.location.href = window.location.href.split('?')[0];
        }
      };
      videoContainer.appendChild(closeButton);
    } catch (error) {
      console.error('Error playing recording:', error);
    }
  };
  
    

  if (loading) {
    return <LoadingSpinner />;
  }

  const groupedRecordings = {};
  recordings.forEach((recording) => {
    const recordingDate = new Date(recording.startTime * 1000).toLocaleDateString(navigator.language, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    if (!groupedRecordings[recordingDate]) {
      groupedRecordings[recordingDate] = [];
    }
    groupedRecordings[recordingDate].push(recording);
  });

  return (
    <div className="content-container">
      <h1>Letzte Ereignisse</h1>
      {Object.entries(groupedRecordings).map(([date, events]) => (
        <div key={date}>
          <h2>{date}</h2>
          {events.map((event) => (
            <div key={event.id} className="recording">
              <div className="thumbnail-container">
                <img src={`data:image/jpeg;base64,${event.snapshot_medium}`} alt="Recording Thumbnail" className="thumbnail-img" />
                <button className="play-button" onClick={() => playRecording(event.id)}>▶</button>
              </div>
              <div className="recording-info">
                <h3>{event.camera_name}</h3>
                <p>Ereigniszeit: {new Date(event.startTime * 1000).toLocaleString('de-DE', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' })}</p>
                <p>Dauer: {formatDuration(event.stopTime - event.startTime)}</p>
                <p>Größe: {(event.event_size_bytes / (1024 * 1024)).toFixed(2)} MB</p>
              </div>
            </div>
          ))}
        </div>
      ))}
      <div className="load-more-container">
        {loadMoreLoading ? (
          <div className="load-more-loading">Laden...</div>
        ) : hasMore ? (
          <button className="load-more-button" onClick={fetchMoreRecordings}>
            Mehr laden
          </button>
        ) : recordings.length > 0 && !hasMore ? (
          <div className="all-loaded">Alle Ereignisse geladen</div>
        ) : null}
      </div>
    </div>
  );
}

export default Recordings;

